import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';

import { INotificationUserEmployeeDTO } from 'src/api/dtos';
import NotificationsRepository from 'src/api/repositories/common/v1/notifications.repository';
import UsersRepository from 'src/api/repositories/common/v1/users.repository';
import { IError } from 'src/api/types';
import { NotificationMessageTypeEnums } from 'src/features/message-center/MessageCenter.enums';
import { mapNotificationMessageTypeEnums } from 'src/features/message-center/MessageCenter.utils';
import { useMessageCenterEdit } from 'src/features/message-center/providers';
import {
  AttachmentIcon,
  CloseFilledIcon,
  CloseIcon,
  ConfirmDeleteFileModal,
  DocumentUploadModal,
  FileChip,
  SendIcon,
} from 'src/shared/components';
import { PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useInfiniteScroll } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { buildRequestFormData, getErrorMessage } from 'src/shared/utils';

import { MessageCenterDetailsToFilters } from './MessageCenterDetailsToFilters';
import { MessageCenterDetailsToInput } from './MessageCenterDetailsToInput';

const MAX_NEW_MESSAGE_LENGTH = 2000;

export const MessageCenterDetailsEdit: FC = () => {
  const { t } = useTranslate();
  const [openDocumentUploadModal, setOpenDocumentUploadModal] = useState<boolean>(false);
  const [deleteFile, setDeleteFile] = useState<File | null>(null);
  const [deleteFileModalOpen, setDeleteFileModalOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const {
    inputs,
    type,
    onUserIdsChange,
    onExcludeUserIdsChange,
    onDepartmentIdsChange,
    onTeamIdsChange,
    onSubjectChange,
    onMessageChange,
    onCancel,
  } = useMessageCenterEdit();

  const hideRecipients =
    !inputs?.notificationId && !inputs?.userIds.length && !inputs?.departmentIds.length && !inputs?.teamIds.length;

  useEffect(() => {
    // Reset 'excludeUserIds' when filters are manually reset (no params)
    if (!hideRecipients) {
      return;
    }

    onExcludeUserIdsChange([]);
  }, [inputs]);

  const {
    collection: recipients,
    paginatedData: paginatedRecipients,
    handleLoadMore,
    isUninitialized,
    isLoadingMore,
    isRefreshing,
  } = useInfiniteScroll<INotificationUserEmployeeDTO>({
    useQuery: UsersRepository.endpoints.useGetUsersQuery,
    apiParams: {
      notificationId: inputs?.notificationId,
      userIds: inputs?.userIds,
      excludeUserIds: inputs?.excludeUserIds,
      departmentIds: inputs?.departmentIds,
      teamIds: inputs?.teamIds,
      perPage: 10,
    },
    skip: !inputs || hideRecipients,
  });

  const loadingRecipients = !isUninitialized && (isLoadingMore || isRefreshing);

  const handleMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (type === NotificationMessageTypeEnums.NEW) {
      onMessageChange(value.slice(0, MAX_NEW_MESSAGE_LENGTH));
      return;
    }

    onMessageChange(value);
  };

  const handleSend = async () => {
    if (!inputs) {
      return;
    }
    setIsCreating(true);
    try {
      const payload = buildRequestFormData({
        attachments: files,
        ...inputs,
      });
      const repo = new NotificationsRepository();
      await repo.upload(payload);

      toast.info(t('messageCenter.youHaveSuccessfullySentAMessage'), {
        type: toast.TYPE.SUCCESS,
      });

      onCancel(true);
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteFile = (file?: File) => {
    if (!file) {
      return;
    }
    setDeleteFile(file);
    setDeleteFileModalOpen(true);
  };

  if (!inputs || type === undefined) {
    return null;
  }

  return (
    <Paper
      sx={{
        padding: 2,
        width: '100%',
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
        maxHeight: '100%',
      }}
    >
      <Stack direction="row" gap={2} justifyContent="space-between">
        <Typography variant="h4" fontSize={24} fontWeight={500}>
          {mapNotificationMessageTypeEnums(type, t)}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="info"
          onClick={() => onCancel()}
          sx={{ width: 40, height: 40, minWidth: 'unset' }}
        >
          <CloseIcon />
        </Button>
      </Stack>
      <Stack direction="column" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t('messageCenter.to')}
        </Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <MessageCenterDetailsToInput
            recipients={hideRecipients ? [] : recipients}
            recipientsCount={paginatedRecipients?.total ?? 0}
            loadingRecipients={loadingRecipients}
            userIds={inputs.userIds}
            excludeUserIds={inputs.excludeUserIds}
            onLoadMoreRecipients={handleLoadMore}
            onUserIdsChange={onUserIdsChange}
            onExcludeUserIdsChange={onExcludeUserIdsChange}
          />
          <MessageCenterDetailsToFilters
            departmentIds={inputs.departmentIds}
            teamIds={inputs.teamIds}
            onDepartmentIdsChange={onDepartmentIdsChange}
            onTeamIdsChange={onTeamIdsChange}
          />
        </Stack>
      </Stack>
      <Stack direction="column" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t('global.subject')}
        </Typography>
        <TextField
          size="small"
          value={inputs.subject}
          onChange={(event) => onSubjectChange(event.target.value)}
          disabled={isCreating}
        />
      </Stack>
      <Stack direction="column" gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t('messageCenter.message')}
        </Typography>
        <TextField value={inputs.message} multiline rows={10} onChange={handleMessageChange} disabled={isCreating} />
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        {type === NotificationMessageTypeEnums.NEW && (
          <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
            {`${inputs.message.length}/${MAX_NEW_MESSAGE_LENGTH}`}
          </Typography>
        )}
      </Stack>
      {!openDocumentUploadModal && (
        <Stack justifyContent="flex-start">
          {!openDocumentUploadModal && (
            <Grid container spacing={2}>
              {files.map((file, index) => (
                <Grid item key={`${file.name}-${index}`} xs={files.length === 1 ? 12 : 6}>
                  <FileChip loading={isCreating} canPreview file={file} onDeleteFile={handleDeleteFile} />
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      )}

      <Stack direction={'row'} justifyContent="space-between" gap={2} width="100%">
        <ComponentWithPermissions permissions={PermissionsEnums.NOTIFICATION_ATTACHMENTS}>
          <Button
            color="secondary"
            disabled={isCreating}
            startIcon={<AttachmentIcon width={20} height={20} />}
            onClick={() => setOpenDocumentUploadModal(true)}
          >
            {t('messageCenter.addAttachments')}
          </Button>
        </ComponentWithPermissions>
        <Stack direction={'row'} justifyContent="flex-end" gap={2}>
          <Button color="secondary" startIcon={<CloseFilledIcon size={20} />} onClick={() => onCancel()}>
            {t('global.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSend}
            loading={isCreating}
            startIcon={<SendIcon size={20} />}
            disabled={hideRecipients || !inputs.subject || !inputs.message}
          >
            {t('messageCenter.send')}
          </LoadingButton>
        </Stack>
      </Stack>
      <DocumentUploadModal
        open={openDocumentUploadModal}
        closeOnSubmit
        confirmOnDelete
        onClose={() => setOpenDocumentUploadModal(false)}
        loading={false}
        files={files}
        setFiles={setFiles}
      />
      <ConfirmDeleteFileModal
        open={deleteFileModalOpen}
        onClose={() => setDeleteFileModalOpen(false)}
        onConfirm={() => {
          if (!deleteFile) {
            return;
          }
          setFiles((prev) => prev.filter((f) => f !== deleteFile));
          setDeleteFileModalOpen(false);
        }}
        fileName={deleteFile?.name || ''}
      />
    </Paper>
  );
};
